import React from 'react';
import { motion } from 'framer-motion';

// COMPLETE sample data for Alex Morgan
const sampleResumeData = {
  name: "Alex Morgan",
  title: "",
  contact: {
    email: "alex.morgan@example.com",
    phone: "(555) 123-4567",
    location: "San Francisco, CA",
    linkedin: "https://linkedin.com/in/alexmorgan",
    github: "https://github.com/alexmorgan",
  },
  summary: "Results-oriented Product Manager with 7+ years of experience leading cross-functional teams in the technology sector. Proven track record of delivering complex SaaS products from conception to launch.",
  experience: [
    {
      title: "Senior Product Manager",
      company: "TechFlow Solutions",
      location: "San Francisco, CA",
      dates: "2020 - Present",
      bullets: [
        "Led a team of 15 developers and designers to launch the company's flagship enterprise SaaS product, resulting in $2M ARR within first year.",
        "Reduced deployment time by 40% through implementation of streamlined agile processes.",
        "Conducted user research and competitive analysis to identify key market opportunities."
      ]
    },
    {
      title: "Product Manager",
      company: "DataDrive Inc.",
      location: "San Francisco, CA",
      dates: "2018 - 2020",
      bullets: [
        "Managed product roadmap for analytics platform serving 50,000+ users.",
        "Increased user engagement by 65% through implementation of personalized dashboard features."
      ]
    }
  ],
  education: [
    {
      degree: "MBA, Business Administration",
      institution: "University of California, Berkeley",
      years: "2017",
      field: "",
      location: "",
      gpa: "3.9/4.0"
    },
    {
      degree: "BS, Computer Science",
      institution: "Stanford University",
      years: "2015",
      field: "",
      location: "",
      gpa: "3.8 CGPA"
    }
  ],
  skills: ["Product Strategy", "Agile/Scrum", "Data Analysis", "SQL", "Python", "User Research", "A/B Testing", "Roadmap Planning"],
  certifications: [
    { name: "Project Management Professional (PMP)", issuer: "PMI" },
    { name: "Certified Scrum Product Owner", issuer: "Scrum Alliance" }
  ],
  projects: [
    {
      name: "Customer Analytics Platform",
      company: "Tata Consultancy Services",
      dates: "2021 - 2022",
      description: "Built end-to-end analytics platform that processes 1M+ events daily, providing real-time insights to enterprise customers. Achieved 99.9% uptime and reduced query latency by 70%.",
      bullets: [],
      links: []
    },
    {
      name: "Mobile App Redesign",
      company: "",
      dates: "",
      description: "Led complete redesign of mobile application, increasing user retention by 45% and achieving 4.8-star rating on app stores.",
      bullets: [],
      links: []
    }
  ],
  publications: [
    {
      title: "Scaling Product Analytics in the Enterprise",
      venue: "Product Management Journal",
      year: "2023",
      link: ""
    }
  ],
  interests: ["Open Source", "Photography", "Trail Running", "Sci-Fi Literature"],
  additionalSections: []
};

const LINK_RE = /(https?:\/\/\S+|linkedin\.com\/\S+|github\.com\/\S+)/i;
const GLOBAL_LINK_RE = /(?:https?:\/\/[^\s,;]+|(?:linkedin|github)\.com\/[^\s,;]+)/gi;
const BULLET_SPLIT_RE = /\n|•|\u2022|\u2023|\u25E6|\u2043|\u2219|·/;
const DATE_SEPARATOR_RE = /[\-\u2013\u2014]/;

const toTitle = (str) =>
  str
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0] ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w)
    .join(' ');

const nameFromEmail = (email) => {
  if (!email || typeof email !== 'string') return '';
  const local = email.split('@')[0] || '';
  const cleaned = local.replace(/[^a-zA-Z]+/g, ' ').trim();
  if (!cleaned) return '';
  return toTitle(cleaned);
};

const splitBulletsFromText = (text) => {
  if (!text) return [];
  const normalized = String(text)
    .replace(/\r/g, '')
    .replace(/(?:^|\s)[-•]\s+/g, '\n')
    .replace(/\t+/g, ' ')
    .trim();
  const parts = normalized
    .split(BULLET_SPLIT_RE)
    .map((l) => l.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : [String(text).trim()];
};

// Ensure bullets is always a flat array of strings
const safeBullets = (bullets) => {
  if (!bullets) return [];
  if (Array.isArray(bullets)) {
    return bullets
      .map((b) => toCleanString(extractText(b)))
      .filter(Boolean);
  }
  if (typeof bullets === 'string') {
    return splitBulletsFromText(bullets);
  }
  return [];
};

// Safe description: handle string or array
const safeDescription = (desc) => {
  if (!desc) return '';
  if (Array.isArray(desc)) return desc.join(' ');
  return String(desc);
};

const stripMarkdownBold = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/_(.*?)_/g, '$1');
};

const stripHtmlTags = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/<\/?strong>/gi, '')
    .replace(/<\/?b>/gi, '')
    .replace(/<\/?em>/gi, '')
    .replace(/<\/?i>/gi, '')
    .replace(/<[^>]*>/g, '');
};

const sanitizeText = (text) => stripHtmlTags(stripMarkdownBold(text));

const sanitizeInlineText = (text) =>
  sanitizeText(text)
    .replace(/\s+/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .trim();

const DATE_RANGE_RE = /(?:(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*\.?\s+)?\d{4}\s*[\-\u2013\u2014]\s*(?:(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*\.?\s+)?(?:\d{4}|[Pp]resent)/i;

// Check if a string is primarily a date range with little else
const isDateOnlyTitle = (str) => {
  if (!str) return false;
  const s = str.trim();
  // Remove date range, see what's left
  const remaining = s.replace(DATE_RANGE_RE, '').trim();
  return DATE_RANGE_RE.test(s) && remaining.length < 5;
};

// Join fragmented bullets
const joinFragmentedBullets = (bullets) => {
  if (!bullets || bullets.length < 2) return bullets || [];
  const result = [bullets[0]];
  for (let i = 1; i < bullets.length; i++) {
    const b = bullets[i];
    const prev = result[result.length - 1];
    // Join if: current starts with lowercase and previous doesn't end with sentence-end punct
    if (b && b[0] && b[0] === b[0].toLowerCase() && b[0] !== b[0].toUpperCase()
        && prev && !prev.match(/[.!?:;]$/)) {
      result[result.length - 1] = prev + ' ' + b;
    } else {
      result.push(b);
    }
  }
  return result;
};

const extractText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    return value.text || value.description || value.summary || value.detail || value.item || value.value || value.name || '';
  }
  return String(value);
};

const normalizeUrl = (value) => {
  const text = sanitizeInlineText(extractText(value));
  if (!text) return '';
  if (/^(?:https?:\/\/|mailto:|tel:)/i.test(text)) return text;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)) return `mailto:${text}`;
  if (/^\+?[0-9()\-\s]+$/.test(text) && text.replace(/\D/g, '').length >= 7) return `tel:${text}`;
  if (/^(?:linkedin|github)\.com\//i.test(text)) return `https://${text}`;
  return text;
};

const cleanVisibleLinkText = (value) => {
  const text = sanitizeInlineText(extractText(value));
  return text.replace(/^(mailto:|tel:)/i, '');
};

const toCleanString = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return sanitizeInlineText(value);
  if (typeof value === 'number') return String(value);
  return sanitizeInlineText(String(value));
};

const normalizeDateRange = (start, end) => {
  const s = toCleanString(start);
  const e = toCleanString(end);
  if (s && e) return `${s} - ${e}`;
  return s || e || '';
};

const getStringList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((v) => toCleanString(extractText(v))).filter(Boolean);
  if (typeof value === 'string') return splitBulletsFromText(value);
  return [];
};

const inferLinkType = (url) => {
  if (!url) return '';
  if (/linkedin\.com/i.test(url)) return 'linkedin';
  if (/github\.com/i.test(url)) return 'github';
  return 'other';
};

const extractLinksFromText = (value) => {
  const text = extractText(value);
  if (!text) return [];
  const matches = text.match(GLOBAL_LINK_RE) || [];
  return matches.map(normalizeUrl).filter(Boolean);
};

const dedupeStrings = (values) => {
  const seen = new Set();
  return values.filter((value) => {
    const key = (value || '').toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const cleanBullet = (value) => {
  const text = toCleanString(value).replace(/^[•*\-]\s*/, '');
  return text;
};

const stripLinksFromText = (value) =>
  toCleanString(value)
    .replace(GLOBAL_LINK_RE, '')
    .replace(/\s+/g, ' ')
    .trim();

const normalizeBulletList = (...values) =>
  dedupeStrings(
    values
      .flatMap((value) => getStringList(value))
      .map((value) => stripLinksFromText(cleanBullet(value)))
      .filter(Boolean)
  );

const isDateOnlyValue = (value) => {
  const text = toCleanString(value);
  if (!text) return false;
  return DATE_RANGE_RE.test(text) && text.replace(DATE_RANGE_RE, '').trim().length === 0;
};

const looksLikeProjectContinuation = (project) => {
  const name = toCleanString(project?.name);
  const bullets = Array.isArray(project?.bullets) ? project.bullets : [];
  const description = toCleanString(project?.description);
  const dates = toCleanString(project?.dates);
  if (!name) return false;
  if (dates) return false;
  if (isDateOnlyValue(name)) return true;
  if (/^[a-z0-9(]/.test(name)) return true;
  if (name.length > 120 && bullets.length === 0 && !description) return true;
  if (/[.:]$/.test(name)) return true;
  if ((bullets.length > 0 || description) && name.split(/\s+/).length <= 12) return false;
  if (bullets.length === 0 && description && name.split(/\s+/).length > 12) return true;
  return false;
};

const mergeProjectIntoPrevious = (previous, current) => {
  const mergedBullets = dedupeStrings([
    ...(previous.bullets || []),
    ...normalizeBulletList(current.name),
    ...(current.description ? normalizeBulletList(current.description) : []),
    ...(current.bullets || []),
  ]);

  return {
    ...previous,
    dates: previous.dates || current.dates || '',
    description: previous.description || '',
    bullets: joinFragmentedBullets(mergedBullets),
    links: dedupeStrings([...(previous.links || []), ...(current.links || [])]),
  };
};

const normalizeResumeForDisplay = (input) => {
  const data = JSON.parse(JSON.stringify(input || {}));
  data.contact = data.contact || {};

  // Map basics/headline/contact (LLM or other parsers)
  if (data.basics) {
    if (!data.name && data.basics.name) data.name = data.basics.name;
    if (!data.title && data.basics.headline) data.title = data.basics.headline;
    const basicsContact = data.basics.contact || {};
    data.contact.email = data.contact.email || basicsContact.email || '';
    data.contact.phone = data.contact.phone || basicsContact.phone || '';
    data.contact.location = data.contact.location || basicsContact.location || '';
    const links = basicsContact.links || [];
    if (Array.isArray(links)) {
      links.forEach((link) => {
        const url = typeof link === 'string' ? link : link?.url || link?.link;
        const type = inferLinkType(url || '');
        if (type === 'linkedin' && !data.contact.linkedin) data.contact.linkedin = normalizeUrl(url);
        if (type === 'github' && !data.contact.github) data.contact.github = normalizeUrl(url);
      });
    }
  }

  // Normalize contact links if provided in a generic array
  if (Array.isArray(data.contact?.links)) {
    data.contact.links.forEach((link) => {
      const url = typeof link === 'string' ? link : link?.url || link?.link;
      const type = inferLinkType(url || '');
      if (type === 'linkedin' && !data.contact.linkedin) data.contact.linkedin = normalizeUrl(url);
      if (type === 'github' && !data.contact.github) data.contact.github = normalizeUrl(url);
    });
  }

  let name = (data.name || '').trim();

  // Extract ALL URLs from name
  let match;
  while ((match = LINK_RE.exec(name)) !== null) {
    const url = match[0];
    if (!data.contact.linkedin && /linkedin\.com/i.test(url)) {
      data.contact.linkedin = normalizeUrl(url);
    }
    if (!data.contact.github && /github\.com/i.test(url)) {
      data.contact.github = normalizeUrl(url);
    }
    name = name.replace(url, '');
    LINK_RE.lastIndex = 0; // reset regex
  }
  // Clean separators and whitespace
  name = name.replace(/[\|\-–—]+/g, ' ').replace(/\s+/g, ' ').trim();

  // If remaining name has pipe separators, split
  if (name && name.includes('|')) {
    const parts = name.split('|').map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1) {
      name = parts[0];
      if (!data.contact.location) data.contact.location = parts[1];
    }
  }

  // If remaining name has " - " separator, split
  if (name && name.includes(' - ')) {
    const parts = name.split(' - ').map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1) {
      name = parts[0];
      if (!data.contact.location) data.contact.location = parts[1];
    }
  }

  // If name looks like just a location (e.g. "Dandeli, Karnataka"), move it
  if (name && /,/.test(name) && !/[0-9]/.test(name) && !data.contact.location) {
    // Check if it looks like "City, State" not a person name
    const commaCount = (name.match(/,/g) || []).length;
    if (commaCount === 1 && name.split(',').every(p => p.trim().length < 25)) {
      data.contact.location = name;
      name = '';
    }
  }

  if (!name) {
    name = nameFromEmail(data.contact.email) || 'Your Name';
  }

  data.name = name;

  // --- Normalize experience ---
  const rawExperience = data.experience || [];

  data.experience = rawExperience.filter((e) => {
    if (typeof e === 'string') return e.trim();
    const hasTitle = e?.title && String(e.title).trim();
    const hasCompany = e?.company && String(e.company).trim();
    const hasBullets =
      (Array.isArray(e?.bullets) && e.bullets.length > 0) ||
      (typeof e?.bullets === 'string' && e.bullets.trim()) ||
      (typeof e?.description === 'string' && e.description.trim()) ||
      (Array.isArray(e?.highlights) && e.highlights.length > 0);
    return hasTitle || hasCompany || hasBullets;
  }).map(exp => {
    if (typeof exp === 'string') {
      return { title: sanitizeText(exp), company: '', location: '', dates: '', bullets: [] };
    }

    const title = toCleanString(exp.title || exp.position || exp.role || exp.job_title);
    const company = toCleanString(exp.company?.name || exp.company || exp.employer || exp.organization || exp.org);
    const location = toCleanString(exp.location || exp.city);
    let dates = toCleanString(exp.dates || exp.date || exp.duration || normalizeDateRange(exp.startDate || exp.start, exp.endDate || exp.end));

    let bullets = normalizeBulletList(
      exp.bullets,
      exp.highlights,
      exp.responsibilities,
      exp.achievements,
      exp.summary,
      exp.details,
      exp.description
    );
    bullets = joinFragmentedBullets(bullets);

    // Fix: if title is actually a date range, move it to dates
    if (title && !dates && isDateOnlyTitle(title)) {
      const dm = title.match(DATE_RANGE_RE);
      if (dm) {
        dates = dm[0];
        const trimmed = title.replace(DATE_RANGE_RE, '').trim();
        return { ...exp, title: trimmed, company, location, dates, bullets };
      }
    }

    // Fix: if title contains a date range embedded, extract it
    if (title && !dates) {
      const dm = title.match(DATE_RANGE_RE);
      if (dm) {
        dates = dm[0];
        const trimmed = title.replace(DATE_RANGE_RE, '').replace(/[\s|\-,\u2013\u2014]+$/, '').trim();
        return { ...exp, title: trimmed, company, location, dates, bullets };
      }
    }

    // Fix: if no title but bullets exist, try to promote first bullet
    if (!title && !company && bullets.length > 0) {
      // If first bullet looks like a role/company (short, no period)
      if (bullets[0].length < 60 && !/[.]/.test(bullets[0])) {
        const trimmed = bullets[0];
        bullets = bullets.slice(1);
        return { ...exp, title: trimmed, company, location, dates, bullets };
      }
    }

    return { ...exp, title, company, location, dates, bullets };
  });

  // Move obvious education-like entries out of Experience
  const eduFromExp = [];
  data.experience = data.experience.filter((exp) => {
    const text = `${exp.title} ${exp.company} ${(exp.bullets || []).join(' ')}`.toLowerCase();
    const isEduKeyword = /college|university|school|institute|academy/.test(text);
    const isEduMetrics = /cgpa|gpa|marks|grade|percentage/.test(text);
    const dates = exp.dates || exp.title || '';
    const isDateRange = DATE_RANGE_RE.test(dates);
    const isLikelyEducation = !exp.company && isDateRange && (isEduKeyword || isEduMetrics);

    if (isLikelyEducation) {
      const inst = (exp.bullets || []).find(b => /college|university|school|institute|academy/i.test(b)) || '';
      eduFromExp.push({
        degree: '',
        institution: inst,
        years: dates,
        field: '',
        location: ''
      });
      return false;
    }
    return true;
  });

  if (eduFromExp.length > 0) {
    data.education = [...(data.education || []), ...eduFromExp];
  }

  // --- Normalize projects ---
  const normalizedProjects = (data.projects || []).filter((p) => {
    if (typeof p === 'string') return p.trim();
    const hasName = p?.name || p?.title || p?.project;
    const hasDesc = p?.description || p?.summary || p?.details || p?.bullets || p?.highlights;
    return Boolean(hasName || hasDesc);
  }).map((p) => {
    if (typeof p === 'string') {
      return { name: sanitizeText(p), description: '', bullets: [], links: [], dates: '' };
    }

    const rawName = toCleanString(p?.name || p?.title || p?.project);
    const descValue = p?.description || p?.summary || p?.details || '';
    const descIsArray = Array.isArray(descValue);
    let bullets = normalizeBulletList(
      p?.bullets,
      p?.highlights,
      p?.responsibilities,
      p?.achievements,
      p?.description_bullets,
      descIsArray ? descValue : []
    );
    bullets = joinFragmentedBullets(bullets);

    const description = descIsArray ? '' : stripLinksFromText(descValue);
    const fallbackBullets = !bullets.length && description
      ? splitBulletsFromText(description).map((value) => stripLinksFromText(cleanBullet(value))).filter(Boolean)
      : [];
    const finalBullets = fallbackBullets.length > 1 ? fallbackBullets : bullets;

    const links = dedupeStrings([
      ...(Array.isArray(p?.links) ? p.links : (p?.links ? [p.links] : []))
        .map(normalizeUrl)
        .filter(Boolean),
      ...extractLinksFromText(rawName),
      ...extractLinksFromText(descValue),
      ...extractLinksFromText(p?.details),
    ]);

    let dates = toCleanString(p?.dates || p?.date || p?.duration || normalizeDateRange(p?.startDate || p?.start, p?.endDate || p?.end));
    let name = rawName;

    if (!dates && rawName) {
      const dateMatch = rawName.match(DATE_RANGE_RE);
      if (dateMatch) {
        dates = dateMatch[0];
        name = rawName.replace(DATE_RANGE_RE, '').replace(/[\s|\-,\u2013\u2014]+$/, '').trim();
      }
    }

    const company = toCleanString(p?.company || p?.client || p?.organization || p?.employer || p?.org);

    return {
      ...p,
      name,
      company,
      description: fallbackBullets.length > 1 ? '' : description,
      bullets: finalBullets,
      links,
      dates,
    };
  });

  data.projects = normalizedProjects.reduce((projects, project) => {
    if (!project) return projects;

    if (isDateOnlyValue(project.name) && !project.dates) {
      const previous = projects[projects.length - 1];
      if (previous) {
        previous.dates = previous.dates || project.name;
      } else {
        projects.push({ ...project, dates: project.name, name: '', description: '', bullets: [] });
      }
      return projects;
    }

    const previous = projects[projects.length - 1];
    if (previous && looksLikeProjectContinuation(project)) {
      projects[projects.length - 1] = mergeProjectIntoPrevious(previous, project);
      return projects;
    }

    if (previous && !previous.name && previous.dates && project.name) {
      projects[projects.length - 1] = { ...project, dates: previous.dates || project.dates || '' };
      return projects;
    }

    projects.push(project);
    return projects;
  }, []).filter((project) => project.name || project.description || (project.bullets && project.bullets.length > 0));

  // --- Normalize certifications ---
  data.certifications = (data.certifications || []).filter((c) => {
    const nameVal = c?.name || c;
    return nameVal && String(nameVal).trim();
  });

  // --- Normalize skills (handle objects with name field) ---
  data.skills = (data.skills || []).map(s => {
    if (typeof s === 'string') return s;
    if (s && typeof s === 'object' && s.name) return String(s.name);
    return '';
  }).map(toCleanString).filter(Boolean);


  // --- Normalize education ---
  data.education = (data.education || []).filter((e) => {
    if (typeof e === 'string') return e.trim();
    const hasDegree = e?.degree || e?.qualification || e?.program || e?.title;
    const hasInstitution = e?.institution || e?.school || e?.college || e?.university || e?.organization || e?.institute;
    return Boolean(hasDegree || hasInstitution);
  }).map((edu) => {
    if (typeof edu === 'string') {
      return { degree: '', institution: sanitizeText(edu), years: '', field: '', location: '' };
    }
    const degree = toCleanString(edu.degree || edu.qualification || edu.program || edu.title);
    const institution = toCleanString(edu.institution || edu.school || edu.college || edu.university || edu.organization || edu.institute);
    const field = toCleanString(edu.field || edu.major || edu.specialization || edu.subject);
    const years = toCleanString(edu.years || edu.year || edu.dates || normalizeDateRange(edu.startDate || edu.start, edu.endDate || edu.end) || edu.graduationDate);
    const location = toCleanString(edu.location || edu.city || edu.state);
    const gpa = toCleanString(edu.gpa || edu.cgpa || edu.grade || edu.score || edu.percentage);
    return { ...edu, degree, institution, field, years, location, gpa };
  });

  // --- Normalize publications ---
  data.publications = (data.publications || []).map((pub) => {
    if (!pub) return null;
    if (typeof pub === 'string') {
      return { title: toCleanString(pub), venue: '', year: '', link: '' };
    }
    const title = toCleanString(pub.title || pub.name || pub.paper);
    const venue = toCleanString(pub.venue || pub.journal || pub.conference || pub.publisher);
    const year = toCleanString(pub.year || pub.date || pub.published);
    const link = normalizeUrl(pub.link || pub.url || pub.doi || '');
    return { ...pub, title, venue, year, link };
  }).filter((pub) => pub && (pub.title || pub.venue));

  // --- Normalize interests ---
  data.interests = (data.interests || data.hobbies || []).map((interest) => {
    if (!interest) return '';
    if (typeof interest === 'string') return toCleanString(interest);
    return toCleanString(interest.name || interest.title || interest.value || '');
  }).filter(Boolean);

  // --- Normalize additional sections ---
  if (!data.additionalSections && data.additional_sections) {
    data.additionalSections = data.additional_sections;
  }
  data.additionalSections = (data.additionalSections || []).map((section) => {
    if (!section) return null;
    const heading = toCleanString(section.heading || section.title || section.label);
    const content = Array.isArray(section.content)
      ? section.content.map(toCleanString).filter(Boolean)
      : toCleanString(section.content || section.value || '');
    return { ...section, heading, content };
  }).filter(Boolean);

  data.contact.email = cleanVisibleLinkText(data.contact.email);
  data.contact.phone = cleanVisibleLinkText(data.contact.phone);
  data.contact.linkedin = normalizeUrl(data.contact.linkedin);
  data.contact.github = normalizeUrl(data.contact.github);
  data.summary = toCleanString(data.summary);

  return data;
};


// Contact item with TEXT LABEL (matches PDF exactly)
const LinkText = ({ href, text, color = '#4f46e5', style = {} }) => {
  if (!href || !text) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      style={{
        color,
        textDecoration: 'none',
        wordBreak: 'break-word',
        ...style,
      }}
    >
      {text}
    </a>
  );
};

const ContactItem = ({ label, text, href, color = '#475569' }) => {
  if (!text) return null;

  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ 
        fontSize: '9px', 
        fontWeight: 700, 
        color: '#4f46e5', 
        textTransform: 'uppercase',
        marginBottom: '2px',
        letterSpacing: '0.3px'
      }}>
        {label}
      </div>
      <div style={{ 
        fontSize: '11px', 
        lineHeight: '1.4', 
        color: color,
        wordBreak: 'break-word'
      }}>
        {href ? <LinkText href={href} text={text} color={color} /> : text}
      </div>
    </div>
  );
};

const PublicationsBlock = ({ publications, HeadingRenderer, heading = 'Publications', textColor = '#4b5563' }) => {
  if (!publications || publications.length === 0) return null;
  return (
    <div style={{ marginTop: '14px' }}>
      <HeadingRenderer>{heading}</HeadingRenderer>
      <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
        {publications.map((pub, i) => {
          const meta = [pub.venue, pub.year].filter(Boolean).join(', ');
          return (
            <li key={i} style={{ fontSize: '11px', color: textColor, marginBottom: '4px', lineHeight: '1.5' }}>
              <span style={{ fontWeight: 600 }}>{pub.title}</span>
              {meta && <span style={{ fontStyle: 'italic' }}> — {meta}</span>}
              {pub.link && (
                <>
                  {' '}
                  <LinkText href={pub.link} text={cleanVisibleLinkText(pub.link)} color="#4f46e5" style={{ fontSize: '10px' }} />
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const InterestsBlock = ({ interests, HeadingRenderer, heading = 'Interests', textColor = '#4b5563', layout = 'inline' }) => {
  if (!interests || interests.length === 0) return null;
  return (
    <div style={{ marginTop: '14px' }}>
      <HeadingRenderer>{heading}</HeadingRenderer>
      {layout === 'inline' ? (
        <div style={{ fontSize: '11px', color: textColor, lineHeight: '1.6' }}>
          {interests.map((it, i) => (
            <span key={i}>{it}{i < interests.length - 1 ? ' • ' : ''}</span>
          ))}
        </div>
      ) : (
        <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
          {interests.map((it, i) => (
            <li key={i} style={{ fontSize: '11px', color: textColor, marginBottom: '3px', lineHeight: '1.5' }}>{it}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AdditionalSectionsBlock = ({ sections, HeadingRenderer, textColor = '#4b5563', wrapStyle = {} }) => {
  if (!sections || sections.length === 0) return null;
  return (
    <div style={{ marginTop: '14px', ...wrapStyle }}>
      {sections.map((section, idx) => (
        <div key={idx} style={{ marginBottom: '14px' }}>
          <HeadingRenderer>{section.heading}</HeadingRenderer>
          {Array.isArray(section.content) ? (
            <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
              {section.content.map((item, i) => (
                <li key={i} style={{ fontSize: '11px', color: textColor, marginBottom: '3px', lineHeight: '1.5' }}>{item}</li>
              ))}
            </ul>
          ) : (
            <p style={{ fontSize: '11px', color: textColor, marginTop: '5px', lineHeight: '1.5' }}>{section.content}</p>
          )}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// UNIVERSAL PROJECT ITEM - shared by all templates
// ============================================================================
// Smartly renders projects regardless of data shape:
//  - Always render project title if present (bold)
//  - If description exists, render below title
//  - If description is an array (bullets), render bullets

const ProjectItem = ({ proj, nameStyle = {}, descStyle = {}, companyStyle = {} }) => {
  const name = (proj.name || '').trim();
  const company = (proj.company || '').trim();
  const dates = (proj.dates || '').trim();
  const desc = safeDescription(proj.description);
  const bullets = Array.isArray(proj.bullets) ? proj.bullets.filter(b => b && b.trim()) : [];
  const hasBullets = bullets.length > 0;
  const links = Array.isArray(proj.links) ? proj.links.map(normalizeUrl).filter(Boolean) : [];

  const defaultNameStyle = { fontWeight: 700, fontSize: '12px', color: '#1e293b', marginBottom: '3px', ...nameStyle };
  const defaultDescStyle = { fontSize: '11px', color: '#64748b', lineHeight: '1.5', margin: 0, fontWeight: 400, ...descStyle };
  const defaultCompanyStyle = { fontSize: '11px', color: '#4f46e5', fontWeight: 600, marginBottom: '4px', ...companyStyle };
  const linkStyle = { fontSize: '10px', lineHeight: '1.4', display: 'inline-block' };
  const dateStyle = { fontSize: '10px', fontWeight: 700, color: '#1e293b', marginBottom: '4px' };

  const linksBlock = links.length > 0 ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
      {links.map((link, i) => (
        <LinkText key={`${link}-${i}`} href={link} text={cleanVisibleLinkText(link)} color={defaultDescStyle.color} style={linkStyle} />
      ))}
    </div>
  ) : null;

  const companyBlock = company ? <div style={defaultCompanyStyle}>{company}</div> : null;

  // Title row: name on left, date on right
  const titleRow = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
      <div style={defaultNameStyle}>{name || ''}</div>
      {dates && <span style={dateStyle}>{dates}</span>}
    </div>
  );

  // Title + description
  if (name && desc) {
    return (
      <>
        {titleRow}
        {companyBlock}
        <p style={defaultDescStyle}>{desc}</p>
        {hasBullets && (
          <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
            {bullets.map((b, i) => (
              <li key={i} style={defaultDescStyle}>{b}</li>
            ))}
          </ul>
        )}
        {linksBlock}
      </>
    );
  }

  // Only description
  if (desc && !name) {
    return (
      <>
        {dates && <div style={dateStyle}>{dates}</div>}
        {hasBullets ? (
          <ul style={{ margin: '0', paddingLeft: '20px', listStyleType: 'disc' }}>
            {bullets.map((b, i) => (
              <li key={i} style={defaultDescStyle}>{b}</li>
            ))}
          </ul>
        ) : (
          <p style={defaultDescStyle}>{desc}</p>
        )}
        {linksBlock}
      </>
    );
  }

  // Only title (but still render bullets if present)
  if (name && !desc) {
    return (
      <>
        {titleRow}
        {companyBlock}
        {hasBullets && (
          <ul style={{ margin: '4px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
            {bullets.map((b, i) => (
              <li key={i} style={defaultDescStyle}>{b}</li>
            ))}
          </ul>
        )}
        {linksBlock}
      </>
    );
  }

  // Fallback
  return (
    <>
      {dates && <div style={dateStyle}>{dates}</div>}
      <p style={defaultDescStyle}>{desc || name}</p>
      {linksBlock}
    </>
  );
};


// MODERN TEMPLATE
const ModernTemplate = ({ data }) => {
  const hasSkills         = data.skills && data.skills.length > 0;
  const hasEducation      = data.education && data.education.length > 0;
  const hasProjects       = data.projects && data.projects.length > 0;
  const hasCertifications = data.certifications && data.certifications.length > 0;
  const hasPublications   = data.publications && data.publications.length > 0;
  const hasInterests      = data.interests && data.interests.length > 0;
  const hasAdditional     = data.additionalSections && data.additionalSections.length > 0;

  const RightHeading = ({ children }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
      <span style={{ width: '3px', height: '18px', background: '#4f46e5', borderRadius: '2px', flexShrink: 0 }}></span>
      <span style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#1e293b', lineHeight: '1.3' }}>{children}</span>
    </div>
  );

  const SideHeading = ({ children }) => (
    <div style={{ fontSize: '13px', fontWeight: 700, color: '#4f46e5', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #c7d2fe', paddingBottom: '8px', marginBottom: '10px', lineHeight: '1.3' }}>{children}</div>
  );

  return (
    <div style={{ minHeight: '1123px', display: 'flex', position: 'relative', fontSize: '11px', lineHeight: '1.5', color: '#1e293b', fontFamily: 'sans-serif' }}>
      {/* LEFT SIDEBAR */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '30%', background: '#f8fafc', borderRight: '1px solid #e2e8f0' }}></div>
      
      <div style={{ width: '30%', position: 'relative', zIndex: 1, flexShrink: 0 }}>
        <div style={{ padding: '24px 20px 40px 20px' }}>
          <div style={{ marginBottom: '22px' }}>
            <SideHeading>Contact</SideHeading>
            <ContactItem label="EMAIL" text={data.contact.email} href={normalizeUrl(data.contact.email)} />
            <ContactItem label="PHONE" text={data.contact.phone} href={normalizeUrl(data.contact.phone)} />
            <ContactItem label="LOCATION" text={data.contact.location} />
            <ContactItem label="LINKEDIN" text={cleanVisibleLinkText(data.contact.linkedin)} href={normalizeUrl(data.contact.linkedin)} color="#4f46e5" />
            <ContactItem label="GITHUB" text={cleanVisibleLinkText(data.contact.github)} href={normalizeUrl(data.contact.github)} color="#4f46e5" />
          </div>

          {hasEducation && (
            <div style={{ marginBottom: '22px' }}>
              <SideHeading>Education</SideHeading>
              {data.education.map((edu, i) => (
                <div key={i} style={{ marginBottom: '10px' }}>
                  <div style={{ fontWeight: 700, fontSize: '12px', color: '#1e293b' }}>{edu.degree}</div>
                  {edu.field && <div style={{ fontSize: '11px', color: '#475569', fontStyle: 'italic' }}>{edu.field}</div>}
                  <div style={{ fontSize: '11px', color: '#64748b' }}>{edu.institution}</div>
                  {edu.years && <div style={{ fontSize: '10px', color: '#94a3b8', fontStyle: 'italic' }}>{edu.years}</div>}
                  {edu.gpa && <div style={{ fontSize: '10px', color: '#4f46e5', fontWeight: 600, marginTop: '2px' }}>{edu.gpa}</div>}
                </div>
              ))}
            </div>
          )}

          {hasSkills && (
            <div style={{ marginBottom: '22px' }}>
              <SideHeading>Skills</SideHeading>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                {data.skills.map((skill, i) => (
                  <span key={i} style={{ 
                    background: '#fff', 
                    border: '1px solid #e2e8f0', 
                    padding: '6px 8px',
                    borderRadius: '4px', 
                    fontSize: '10px', 
                    color: '#475569',
                    lineHeight: '1'
                  }}>{skill}</span>
                ))}
              </div>
            </div>
          )}

          {hasCertifications && (
            <div style={{ marginBottom: '22px' }}>
              <SideHeading>Certifications</SideHeading>
              {data.certifications.map((cert, i) => (
                <div key={i} style={{ marginBottom: '6px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#1e293b' }}>{cert.name || cert}</div>
                  {cert.issuer && <div style={{ fontSize: '10px', color: '#64748b' }}>{cert.issuer}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT CONTENT */}
      <div style={{ width: '70%', padding: '28px 28px 60px 32px', position: 'relative', zIndex: 1 }}>
        <div style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: '14px', marginBottom: '18px' }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#1e293b', lineHeight: '1.2' }}>{data.name}</div>
          {data.title && <div style={{ fontSize: '16px', color: '#4f46e5', fontWeight: 600, marginTop: '4px' }}>{data.title}</div>}
        </div>

        {data.summary && (
          <div style={{ marginBottom: '18px' }}>
            <RightHeading>Professional Summary</RightHeading>
            <p style={{ fontSize: '11px', color: '#64748b', textAlign: 'justify', lineHeight: '1.6', margin: 0 }}>{data.summary}</p>
          </div>
        )}

        {data.experience && data.experience.length > 0 && (
          <div style={{ marginBottom: '18px' }}>
            <RightHeading>Work Experience</RightHeading>
            {data.experience.map((exp, i) => (
              <div key={i} style={{ marginBottom: '14px' }}>
                {(exp.title || exp.dates) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span style={{ fontWeight: 700, fontSize: '12px', color: '#1e293b' }}>{exp.title}</span>
                    <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 700 }}>{exp.dates}</span>
                  </div>
                )}
                {(exp.company || exp.location) && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '2px' }}>
                    <span style={{ fontSize: '11px', color: '#4f46e5', fontWeight: 600 }}>{exp.company}</span>
                    {exp.location && <span style={{ fontSize: '10px', color: '#94a3b8', fontStyle: 'italic' }}>{exp.location}</span>}
                  </div>
                )}
                {exp.bullets && exp.bullets.length > 0 && (
                  <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
                    {exp.bullets.map((item, j) => (
                      <li key={j} style={{ fontSize: '11px', color: '#64748b', marginBottom: '3px', lineHeight: '1.5' }}>{item}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}

        {hasProjects && (
          <div style={{ marginBottom: '18px' }}>
            <RightHeading>Projects</RightHeading>
            {data.projects.map((proj, i) => (
              <div key={i} style={{ marginBottom: '10px' }}>
                <ProjectItem
                  proj={proj}
                  nameStyle={{ color: '#1e293b' }}
                  descStyle={{ color: '#64748b' }}
                  companyStyle={{ color: '#4f46e5' }}
                />
              </div>
            ))}
          </div>
        )}

        {hasPublications && (
          <PublicationsBlock publications={data.publications} HeadingRenderer={RightHeading} textColor="#64748b" />
        )}

        {hasInterests && (
          <InterestsBlock interests={data.interests} HeadingRenderer={RightHeading} textColor="#64748b" layout="inline" />
        )}

        {hasAdditional && (
          <AdditionalSectionsBlock sections={data.additionalSections} HeadingRenderer={RightHeading} textColor="#64748b" />
        )}
      </div>
    </div>
  );
};

// PROFESSIONAL TEMPLATE
const ProfessionalTemplate = ({ data }) => {
  const hasSkills         = data.skills && data.skills.length > 0;
  const hasProjects       = data.projects && data.projects.length > 0;
  const hasCertifications = data.certifications && data.certifications.length > 0;
  const hasPublications   = data.publications && data.publications.length > 0;
  const hasInterests      = data.interests && data.interests.length > 0;
  const hasAdditional     = data.additionalSections && data.additionalSections.length > 0;

  const SectionHeading = ({ children }) => (
    <div style={{ fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', borderBottom: '1px solid #d1d5db', paddingBottom: '8px', marginBottom: '10px', color: '#111827', lineHeight: '1.3' }}>{children}</div>
  );

  return (
    <div style={{ minHeight: '1123px', background: '#fff', padding: '36px 40px 60px 40px', color: '#111827', fontSize: '11px', lineHeight: '1.5', fontFamily: 'serif' }}>
      <div style={{ textAlign: 'center', borderBottom: '2px solid #111827', paddingBottom: '16px', marginBottom: '22px' }}>
        <div style={{ fontSize: '28px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: '1.2', marginBottom: '10px' }}>{data.name}</div>
        {data.title && <div style={{ fontSize: '14px', color: '#4b5563', fontWeight: 600, marginBottom: '10px' }}>{data.title}</div>}
        
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: '12px', fontSize: '11px', color: '#6b7280', marginTop: '8px' }}>
          {data.contact.email && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <span style={{ fontWeight: 700, fontSize: '8px', textTransform: 'uppercase', color: '#4b5563' }}>EMAIL:</span>
                <LinkText href={normalizeUrl(data.contact.email)} text={data.contact.email} color="inherit" />
            </div>
          )}
          
          {data.contact.phone && data.contact.email && (
            <span style={{ color: '#9ca3af', fontSize: '10px' }}>•</span>
          )}
          
          {data.contact.phone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <span style={{ fontWeight: 700, fontSize: '8px', textTransform: 'uppercase', color: '#4b5563' }}>PHONE:</span>
                <LinkText href={normalizeUrl(data.contact.phone)} text={data.contact.phone} color="inherit" />
            </div>
          )}
          
          {data.contact.location && (data.contact.email || data.contact.phone) && (
            <span style={{ color: '#9ca3af', fontSize: '10px' }}>•</span>
          )}
          
          {data.contact.location && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <span style={{ fontWeight: 700, fontSize: '8px', textTransform: 'uppercase', color: '#4b5563' }}>LOCATION:</span>
              <span>{data.contact.location}</span>
            </div>
          )}
        </div>
        
        {(data.contact.linkedin || data.contact.github) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: '12px', fontSize: '10px', color: '#6b7280', marginTop: '6px' }}>
            {data.contact.linkedin && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <span style={{ fontWeight: 700, fontSize: '8px', textTransform: 'uppercase', color: '#4b5563' }}>LINKEDIN:</span>
                <LinkText href={normalizeUrl(data.contact.linkedin)} text={cleanVisibleLinkText(data.contact.linkedin)} color="#4f46e5" style={{ fontSize: '9px' }} />
              </div>
            )}
            
            {data.contact.github && data.contact.linkedin && (
              <span style={{ color: '#9ca3af' }}>•</span>
            )}
            
            {data.contact.github && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <span style={{ fontWeight: 700, fontSize: '8px', textTransform: 'uppercase', color: '#4b5563' }}>GITHUB:</span>
                <LinkText href={normalizeUrl(data.contact.github)} text={cleanVisibleLinkText(data.contact.github)} color="#4f46e5" style={{ fontSize: '9px' }} />
              </div>
            )}
          </div>
        )}
      </div>

      {data.summary && (
        <div style={{ marginBottom: '20px' }}>
          <SectionHeading>Summary</SectionHeading>
          <p style={{ textAlign: 'justify', lineHeight: '1.6' }}>{data.summary}</p>
        </div>
      )}

      {data.experience && data.experience.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <SectionHeading>Experience</SectionHeading>
          {data.experience.map((exp, i) => (
            <div key={i} style={{ marginBottom: '14px' }}>
              {(exp.company || exp.dates) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '12px' }}>
                  <span>{exp.company}</span>
                  <span style={{ fontWeight: 700 }}>{exp.dates}</span>
                </div>
              )}
              {(exp.title || exp.location) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontStyle: 'italic', color: '#374151', fontSize: '11px', marginTop: '2px' }}>
                  <span>{exp.title}</span>
                  {exp.location && <span>{exp.location}</span>}
                </div>
              )}
              {exp.bullets && exp.bullets.length > 0 && (
                <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
                  {exp.bullets.map((item, j) => (
                    <li key={j} style={{ fontSize: '11px', color: '#374151', marginBottom: '3px', lineHeight: '1.5' }}>{item}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {hasProjects && (
        <div style={{ marginBottom: '20px' }}>
          <SectionHeading>Projects</SectionHeading>
          {data.projects.map((proj, i) => (
            <div key={i} style={{ marginBottom: '10px' }}>
              <ProjectItem
                proj={proj}
                nameStyle={{ color: '#111827' }}
                descStyle={{ color: '#374151' }}
                companyStyle={{ color: '#374151', fontStyle: 'italic' }}
              />
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '32px', marginBottom: '20px' }}>
        {data.education && data.education.length > 0 && (
          <div style={{ flex: 1 }}>
            <SectionHeading>Education</SectionHeading>
            {data.education.map((edu, i) => (
              <div key={i} style={{ marginBottom: '10px' }}>
                <div style={{ fontWeight: 700, fontSize: '12px' }}>{edu.institution}</div>
                <div style={{ fontStyle: 'italic', fontSize: '11px' }}>{edu.degree}{edu.field ? ` in ${edu.field}` : ''}</div>
                {edu.years && <div style={{ color: '#6b7280', fontSize: '10px', marginTop: '2px' }}>{edu.years}</div>}
                {edu.gpa && <div style={{ color: '#111827', fontSize: '10px', fontWeight: 600, marginTop: '2px' }}>{edu.gpa}</div>}
              </div>
            ))}
          </div>
        )}
        {hasSkills && (
          <div style={{ flex: 1 }}>
            <SectionHeading>Skills</SectionHeading>
            <div style={{ fontSize: '11px', color: '#374151', lineHeight: '1.9' }}>
              {data.skills.map((skill, i) => (
                <span key={i}>{skill}{i < data.skills.length - 1 ? ' • ' : ''}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {hasCertifications && (
        <div style={{ marginBottom: '20px' }}>
          <SectionHeading>Certifications</SectionHeading>
          {data.certifications.map((cert, i) => (
            <div key={i} style={{ fontSize: '11px', marginBottom: '4px' }}>
              • {cert.name || cert}{cert.issuer ? ` - ${cert.issuer}` : ''}
            </div>
          ))}
        </div>
      )}

      {hasPublications && (
        <PublicationsBlock publications={data.publications} HeadingRenderer={SectionHeading} textColor="#374151" />
      )}

      {hasInterests && (
        <InterestsBlock interests={data.interests} HeadingRenderer={SectionHeading} textColor="#374151" layout="inline" />
      )}

      {hasAdditional && (
        <AdditionalSectionsBlock sections={data.additionalSections} HeadingRenderer={SectionHeading} textColor="#374151" />
      )}
    </div>
  );
};

// CREATIVE TEMPLATE
const CreativeTemplate = ({ data }) => {
  const hasSkills         = data.skills && data.skills.length > 0;
  const hasProjects       = data.projects && data.projects.length > 0;
  const hasCertifications = data.certifications && data.certifications.length > 0;
  const hasPublications   = data.publications && data.publications.length > 0;
  const hasInterests      = data.interests && data.interests.length > 0;
  const hasAdditional     = data.additionalSections && data.additionalSections.length > 0;

  const CreativeHeading = ({ children }) => (
    <div style={{ fontSize: '13px', fontWeight: 700, color: '#6d28d9', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px', lineHeight: '1.3' }}>{children}</div>
  );

  return (
    <div style={{ minHeight: '1123px', position: 'relative', fontSize: '11px', lineHeight: '1.5', fontFamily: 'sans-serif' }}>
      {/* Absolute background gradient extending full height */}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #ede9fe 0%, #fce7f3 100%)' }}></div>
      
      <div style={{ position: 'relative', zIndex: 1, padding: '20px 20px 60px 20px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: '26px 26px 180px 26px' }}>

          <div style={{ background: 'linear-gradient(90deg, #7c3aed, #db2777)', color: '#fff', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '8px', lineHeight: '1.2' }}>{data.name}</div>
            {data.title && <div style={{ fontSize: '15px', opacity: 0.9, fontWeight: 600, marginBottom: '12px' }}>{data.title}</div>}
            
            {/* Contact Info - HORIZONTAL WITH TEXT LABELS */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', fontSize: '11px', opacity: 0.95, marginTop: '10px' }}>
              {data.contact.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ fontWeight: 700, fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>EMAIL:</span>
                  <LinkText href={normalizeUrl(data.contact.email)} text={data.contact.email} color="inherit" />
                </div>
              )}
              
              {data.contact.phone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ fontWeight: 700, fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>PHONE:</span>
                  <LinkText href={normalizeUrl(data.contact.phone)} text={data.contact.phone} color="inherit" />
                </div>
              )}
              
              {data.contact.location && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span style={{ fontWeight: 700, fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>LOCATION:</span>
                  <span>{data.contact.location}</span>
                </div>
              )}
            </div>
            
            {/* LinkedIn and GitHub on second line */}
            {(data.contact.linkedin || data.contact.github) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', fontSize: '10px', opacity: 0.9, marginTop: '6px' }}>
                {data.contact.linkedin && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ fontWeight: 700, fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>LINKEDIN:</span>
                    <LinkText href={normalizeUrl(data.contact.linkedin)} text={cleanVisibleLinkText(data.contact.linkedin)} color="inherit" />
                  </div>
                )}
                
                {data.contact.github && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ fontWeight: 700, fontSize: '8px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>GITHUB:</span>
                    <LinkText href={normalizeUrl(data.contact.github)} text={cleanVisibleLinkText(data.contact.github)} color="inherit" />
                  </div>
                )}
              </div>
            )}
          </div>

          {data.summary && (
            <div style={{ marginBottom: '16px' }}>
              <CreativeHeading>About</CreativeHeading>
              <p style={{ color: '#64748b', textAlign: 'justify', lineHeight: '1.6', margin: 0 }}>{data.summary}</p>
            </div>
          )}

          {data.experience && data.experience.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <CreativeHeading>Experience</CreativeHeading>
              {data.experience.map((exp, i) => (
                <div key={i} style={{ background: '#f5f3ff', borderRadius: '8px', padding: '12px', marginBottom: '8px' }}>
                  {(exp.title || exp.company || exp.dates) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        {exp.title && <div style={{ fontWeight: 700, fontSize: '12px', color: '#1e293b' }}>{exp.title}</div>}
                        {exp.company && <div style={{ fontSize: '11px', color: '#db2777', fontWeight: 600 }}>{exp.company}</div>}
                      </div>
                      {exp.dates && <span style={{ fontSize: '10px', color: '#64748b', whiteSpace: 'nowrap', fontWeight: 700 }}>{exp.dates}</span>}
                    </div>
                  )}
                  {exp.bullets && exp.bullets.length > 0 && (
                    <ul style={{ margin: '6px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
                      {exp.bullets.map((item, j) => (
                        <li key={j} style={{ fontSize: '11px', color: '#64748b', marginBottom: '3px', lineHeight: '1.5' }}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {hasProjects && (
            <div style={{ marginBottom: '16px' }}>
              <CreativeHeading>Projects</CreativeHeading>
              {data.projects.map((proj, i) => (
                <div key={i} style={{ background: '#fdf2f8', borderRadius: '8px', padding: '10px', marginBottom: '6px' }}>
                  <ProjectItem
                    proj={proj}
                    nameStyle={{ color: '#1e293b' }}
                    descStyle={{ color: '#64748b' }}
                    companyStyle={{ color: '#db2777' }}
                  />
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '16px', marginBottom: (hasAdditional || hasCertifications) ? '16px' : '0' }}>
            {data.education && data.education.length > 0 && (
              <div style={{ flex: 1, background: '#f8fafc', borderRadius: '8px', padding: '12px' }}>
                <CreativeHeading>Education</CreativeHeading>
                {data.education.map((edu, i) => (
                  <div key={i} style={{ marginBottom: '7px' }}>
                    <div style={{ fontWeight: 700, fontSize: '11px', color: '#1e293b' }}>{edu.degree}</div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>{edu.institution}</div>
                    {edu.years && <div style={{ fontSize: '10px', color: '#94a3b8' }}>{edu.years}</div>}
                    {edu.gpa && <div style={{ fontSize: '10px', color: '#7c3aed', fontWeight: 600 }}>{edu.gpa}</div>}
                  </div>
                ))}
              </div>
            )}
            {hasSkills && (
              <div style={{ flex: 1, background: '#f8fafc', borderRadius: '8px', padding: '12px' }}>
                <CreativeHeading>Skills</CreativeHeading>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                  {data.skills.map((skill, i) => (
                    <span key={i} style={{ background: '#fff', border: '1px solid #ede9fe', padding: '6px 8px', borderRadius: '4px', fontSize: '10px', color: '#475569', fontWeight: 500, lineHeight: '1' }}>{skill}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {hasInterests && (
            <InterestsBlock interests={data.interests} HeadingRenderer={CreativeHeading} textColor="#64748b" layout="inline" />
          )}

          {hasPublications && (
            <PublicationsBlock publications={data.publications} HeadingRenderer={CreativeHeading} textColor="#64748b" />
          )}

          {hasCertifications && (
            <div style={{ marginBottom: '16px' }}>
              <CreativeHeading>Certifications</CreativeHeading>
              {data.certifications.map((cert, i) => (
                <div key={i} style={{ fontSize: '11px', color: '#475569', marginBottom: '4px' }}>
                  • {cert.name || cert}{cert.issuer ? ` - ${cert.issuer}` : ''}
                </div>
              ))}
            </div>
          )}

          {hasAdditional && (
            <AdditionalSectionsBlock sections={data.additionalSections} HeadingRenderer={CreativeHeading} textColor="#64748b" />
          )}
        </div>
      </div>
    </div>
  );
};
// MINIMAL TEMPLATE
const MinimalTemplate = ({ data }) => {
  const hasSkills         = data.skills && data.skills.length > 0;
  const hasProjects       = data.projects && data.projects.length > 0;
  const hasCertifications = data.certifications && data.certifications.length > 0;
  const hasPublications   = data.publications && data.publications.length > 0;
  const hasInterests      = data.interests && data.interests.length > 0;
  const hasAdditional     = data.additionalSections && data.additionalSections.length > 0;

  const MinimalHeading = ({ children }) => (
    <div style={{ fontWeight: 700, color: '#000', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '12px', marginBottom: '8px', lineHeight: '1.3' }}>{children}</div>
  );

  return (
    <div style={{ minHeight: '1123px', background: '#fff', padding: '36px 36px 60px 36px', color: '#000', fontFamily: 'sans-serif', fontSize: '11px', lineHeight: '1.5', boxSizing: 'border-box' }}>
      <div style={{ marginBottom: '28px' }}>
        <div style={{ fontSize: '32px', fontWeight: 300, letterSpacing: '-0.5px', color: '#000', lineHeight: '1.2' }}>{data.name}</div>
        {data.title && <div style={{ fontSize: '15px', color: '#6b7280', fontWeight: 300, marginTop: '4px' }}>{data.title}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px 20px', maxWidth: '500px', marginTop: '10px', fontSize: '11px', color: '#6b7280', fontWeight: 300 }}>
          {data.contact.email    && <LinkText href={normalizeUrl(data.contact.email)} text={data.contact.email} color="inherit" />}
          {data.contact.phone    && <LinkText href={normalizeUrl(data.contact.phone)} text={data.contact.phone} color="inherit" />}
          {data.contact.location && <span>{data.contact.location}</span>}
          {data.contact.linkedin && <LinkText href={normalizeUrl(data.contact.linkedin)} text={cleanVisibleLinkText(data.contact.linkedin)} color="inherit" style={{ fontSize: '9px', wordBreak: 'break-all' }} />}
          {data.contact.github && <LinkText href={normalizeUrl(data.contact.github)} text={cleanVisibleLinkText(data.contact.github)} color="inherit" style={{ fontSize: '9px', wordBreak: 'break-all' }} />}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: '32px' }}>
        <div>
          {data.education && data.education.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <MinimalHeading>Education</MinimalHeading>
              {data.education.map((edu, i) => (
                <div key={i} style={{ marginBottom: '8px' }}>
                  <div style={{ fontWeight: 500, fontSize: '11px', color: '#000' }}>{edu.institution}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>{edu.degree}{edu.field ? ` - ${edu.field}` : ''}</div>
                  {edu.years && <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>{edu.years}</div>}
                  {edu.gpa && <div style={{ fontSize: '10px', color: '#000', fontWeight: 500, marginTop: '2px' }}>{edu.gpa}</div>}
                </div>
              ))}
            </div>
          )}

          {hasSkills && (
            <div style={{ marginBottom: '24px' }}>
              <MinimalHeading>Skills</MinimalHeading>
              {data.skills.map((skill, i) => (
                <div key={i} style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>{skill}</div>
              ))}
            </div>
          )}

          {hasCertifications && (
            <div style={{ marginBottom: '24px' }}>
              <MinimalHeading>Certifications</MinimalHeading>
              {data.certifications.map((cert, i) => (
                <div key={i} style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
                  {cert.name || cert}{cert.issuer ? ` - ${cert.issuer}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          {data.summary && (
            <div style={{ marginBottom: '24px' }}>
              <MinimalHeading>Profile</MinimalHeading>
              <p style={{ color: '#6b7280', textAlign: 'justify', lineHeight: '1.6' }}>{data.summary}</p>
            </div>
          )}

          {data.experience && data.experience.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <MinimalHeading>Experience</MinimalHeading>
              {data.experience.map((exp, i) => (
                <div key={i} style={{ marginBottom: '14px' }}>
                  {(exp.title || exp.dates) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <span style={{ fontSize: '12px', fontWeight: 500, color: '#000' }}>{exp.title}</span>
                      <span style={{ fontSize: '10px', color: '#9ca3af', fontWeight: 700 }}>{exp.dates}</span>
                    </div>
                  )}
                  {(exp.company || exp.location) && (
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{exp.company}{exp.location ? ` - ${exp.location}` : ''}</div>
                  )}
                  {exp.bullets && exp.bullets.length > 0 && (
                    <ul style={{ margin: '5px 0 0 0', paddingLeft: '20px', listStyleType: 'disc' }}>
                      {exp.bullets.map((item, j) => (
                        <li key={j} style={{ fontSize: '11px', color: '#6b7280', marginBottom: '3px', lineHeight: '1.5' }}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {hasProjects && (
            <div style={{ marginBottom: '24px' }}>
              <MinimalHeading>Projects</MinimalHeading>
              {data.projects.map((proj, i) => (
                <div key={i} style={{ marginBottom: '10px' }}>
                  <ProjectItem
                    proj={proj}
                    nameStyle={{ fontWeight: 500, color: '#000' }}
                    descStyle={{ color: '#6b7280' }}
                    companyStyle={{ color: '#6b7280', fontWeight: 500 }}
                  />
                </div>
              ))}
            </div>
          )}

          {hasPublications && (
            <PublicationsBlock publications={data.publications} HeadingRenderer={MinimalHeading} textColor="#6b7280" />
          )}

          {hasInterests && (
            <InterestsBlock interests={data.interests} HeadingRenderer={MinimalHeading} textColor="#6b7280" layout="inline" />
          )}

          {hasAdditional && (
            <AdditionalSectionsBlock sections={data.additionalSections} HeadingRenderer={MinimalHeading} textColor="#6b7280" />
          )}
        </div>
      </div>
    </div>
  );
};


// Main component
const ResumePreview = ({ template, resumeData, loading = false, viewOnly = false }) => {
  const data = normalizeResumeForDisplay(resumeData || sampleResumeData);

  if (loading) {
    return (
      <div className="w-full flex flex-col items-center justify-center bg-gray-50 rounded-lg" style={{ aspectRatio: '210/297' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
        <p className="text-sm text-gray-600 font-medium">Processing your resume...</p>
        <p className="text-xs text-gray-500 mt-1">This may take a few moments</p>
      </div>
    );
  }

  const renderTemplate = () => {
    switch (template) {
      case 'modern':       return <ModernTemplate data={data} />;
      case 'professional': return <ProfessionalTemplate data={data} />;
      case 'creative':     return <CreativeTemplate data={data} />;
      case 'minimal':      return <MinimalTemplate data={data} />;
      default:             return <ModernTemplate data={data} />;
    }
  };

  const A4_W = 794;
  const A4_H = 1123;

  const card = (
    <div
      className="bg-white shadow-2xl origin-top"
      id="resume-preview-card"
      style={{ width: A4_W, minHeight: A4_H, overflow: 'visible', flexShrink: 0 }}
    >
      {renderTemplate()}
    </div>
  );

  if (viewOnly) return card;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full bg-slate-100 p-4 rounded-xl border border-slate-200 flex justify-center"
      style={{ maxHeight: '90vh', overflow: 'auto' }}
    >
      <div style={{ width: '100%', maxWidth: A4_W }}>
        {card}
      </div>
    </motion.div>
  );
};

export default ResumePreview;


